import { PHASE, PLAYER } from "../../common/constants.ts";
import { COLLEGE_TEAMS } from "../../common/college/d1Data.ts";
import {
	collegeDisplayName,
	resolveCollegeTeam,
} from "../../common/college/resolveCollege.ts";
import { idb } from "../db/index.ts";
import { g } from "../util/index.ts";
import type { UpdateEvents, Player } from "../../common/types.ts";
import addFirstNameShort from "../util/addFirstNameShort.ts";

const getSeason = async (playersAll: Player[], season: number) => {
	const playersAllFiltered = playersAll.filter((p) => p.draft.year === season);
	const byPid = new Map(playersAllFiltered.map((p) => [p.pid, p]));
	const players = await idb.getCopies.playersPlus(playersAllFiltered, {
		attrs: [
			"pid",
			"firstName",
			"lastName",
			"age",
			"valueFuzz",
			"watch",
			"born",
			"college",
		],
		ratings: ["ovr", "pot", "skills", "fuzz", "pos"],
		showNoStats: true,
		showRookies: true,
		fuzz: true,
	});
	players.sort((a, b) => b.valueFuzz - a.valueFuzz);

	const players2 = addFirstNameShort(
		players.map((pa, i) => {
			const raw = byPid.get(pa.pid) as any;
			const resolved =
				(typeof raw?.collegeTid === "number"
					? COLLEGE_TEAMS.find((t) => t.tid === raw.collegeTid)
					: undefined) ?? resolveCollegeTeam(pa.college);
			return {
				pid: pa.pid,
				firstName: pa.firstName,
				lastName: pa.lastName,
				age: pa.age,
				watch: pa.watch,
				valueFuzz: pa.valueFuzz,
				born: pa.born,
				college: resolved ? collegeDisplayName(resolved) : pa.college,
				collegeTid: resolved?.tid,
				collegeYear: (raw as any)?.collegeYear as string | undefined,
				// Ratings - just take the only entry
				ovr: pa.ratings.at(-1).ovr,
				pot: pa.ratings.at(-1).pot,
				skills: pa.ratings.at(-1).skills,
				pos: pa.ratings.at(-1).pos,
				rank: i + 1,
			};
		}),
	);

	return {
		players: players2,
		season,
	};
};

const updateDraftScouting = async (
	inputs: unknown,
	updateEvents: UpdateEvents,
) => {
	if (
		updateEvents.includes("firstRun") ||
		updateEvents.includes("playerMovement")
	) {
		const fantasyDraft = g.get("phase") === PHASE.FANTASY_DRAFT;

		// In fantasy draft, use temp tid
		const tid = fantasyDraft ? PLAYER.UNDRAFTED_FANTASY_TEMP : PLAYER.UNDRAFTED;

		// Once a new draft class is generated, if the next season hasn't started, need to bump up year numbers
		const seasonOffset = g.get("phase") >= PHASE.RESIGN_PLAYERS ? 1 : 0;

		const firstSeason = g.get("season") + seasonOffset;

		const players = (
			await idb.cache.players.indexGetAll("playersByDraftYearRetiredYear", [
				[firstSeason],
				[Infinity, Infinity],
			])
		).filter((p) => p.tid === tid);

		let maxDraftYear = firstSeason + 2;
		for (const p of players) {
			if (p.draft.year > maxDraftYear) {
				maxDraftYear = p.draft.year;
			}
		}

		const seasons: Awaited<ReturnType<typeof getSeason>>[] = [];
		for (let season = firstSeason; season <= maxDraftYear; season++) {
			seasons.push(await getSeason(players, season));
		}

		return {
			fantasyDraft,
			seasons,
		};
	}
};

export default updateDraftScouting;
